import {
  Alert,
  Box,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type {
  FactorStatus,
  ParticipantFactorRow,
  ParticipantFactorStatus,
} from "../../../Redux/api/randomizationApi";

/**
 * One row per stratification factor, for the randomization dialog. SFT-25.
 *
 * THIS TABLE EXISTS TO REPLACE AN ERROR WITH A SCREEN. Pressing Randomize on a
 * participant whose Week 4 form is not in returns `STRATUM_UNRESOLVED` — a
 * message naming no question, no visit and no remedy, for a state that is
 * usually normal. The table answers the three questions a coordinator actually
 * has: which factor, where is it recorded, and is it in yet.
 *
 * THE SOURCE COLUMN IS NOT DECORATION. "Not answered" against a Week 4
 * question means the visit has not happened; against a screening question it
 * means somebody has to go and fill a form in. Same status, opposite actions,
 * and the only thing distinguishing them is the visit name.
 *
 * THE VALUE COLUMN IS ABSENT, NOT BLANK, without `randomization.stratum_view`.
 * The server deletes the keys rather than nulling them (spec 11.2), so the
 * column itself is not rendered — a column of dashes says the participant has
 * no values, which is a different and wrong statement.
 *
 * AFTER ALLOCATION THE ROWS ARE THE SNAPSHOT. They come from
 * `randomization_allocation_factors`, so they describe what the participant
 * was randomized on. Editing a factor answer afterwards is refused outright,
 * but a correction under a protocol deviation still must not silently rewrite
 * the history of an allocation that has already consumed a slot.
 *
 * @category Randomization
 */

const STATUS_COLOR: Record<
  FactorStatus,
  "success" | "default" | "warning" | "error"
> = {
  completed: "success",
  not_answered: "default",
  not_covered: "error",
  out_of_range: "error",
  site_unknown: "warning",
};

export interface FactorReadinessTableProps {
  status?: ParticipantFactorStatus;
  loading?: boolean;
  /**
   * Opens the form the question sits on. Optional: the panel is rendered
   * inside a form question too, where navigating away mid-answer is not
   * something to offer.
   */
  onOpenQuestion?: (row: ParticipantFactorRow) => void;
}

const sourceOf = (row: ParticipantFactorRow, siteLabel: string): string => {
  if (row.source === "site") return siteLabel;
  return [row.phaseName, row.formName].filter(Boolean).join(" → ") || "—";
};

export const FactorReadinessTable = ({
  status,
  loading,
  onOpenQuestion,
}: FactorReadinessTableProps) => {
  const { t } = useTranslation("randomization");

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("ui.loading")}
      </Typography>
    );
  }

  if (!status) return null;

  /*
   * NO ACTIVE VERSION IS A CONFIGURATION PROBLEM, NOT A PARTICIPANT ONE, and
   * the remedy belongs to a different person. A coordinator told "this
   * participant is not ready" would go chasing a site for an answer that would
   * not help; what is actually needed is for somebody with
   * `randomization.config_manage` to activate the committed design.
   */
  if (status.reasons.includes("NO_ACTIVE_CONFIG")) {
    return <Alert severity="warning">{t("ui.participant.noActiveConfig")}</Alert>;
  }

  if (status.factors.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("ui.participant.unstratified")}
      </Typography>
    );
  }

  // Present on every row or none: the server strips the keys for the whole
  // response, so testing the first row is testing the permission.
  const showValues = status.factors.some((row) => "valueLabel" in row);

  return (
    <Box>
      {status.allocated ? (
        <Typography variant="caption" color="text.secondary">
          {t("ui.participant.factorsAsAllocated", {
            version: status.configVersion ?? "—",
          })}
        </Typography>
      ) : null}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60 }}>{t("ui.form.factorCode")}</TableCell>
            <TableCell>{t("ui.participant.factorName")}</TableCell>
            <TableCell>{t("ui.participant.factorSource")}</TableCell>
            {showValues ? (
              <TableCell>{t("ui.participant.factorValue")}</TableCell>
            ) : null}
            <TableCell sx={{ width: 150 }}>
              {t("ui.participant.factorStatus")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {status.factors.map((row) => (
            <TableRow key={row.factorCode}>
              <TableCell>
                <Chip size="small" label={row.factorCode} />
              </TableCell>

              <TableCell>
                <Typography variant="body2">{row.label ?? "—"}</Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {sourceOf(row, t("ui.form.factorSourceSite"))}
                </Typography>
                {/*
                  The link opens the form the answer belongs on. Offered only
                  for an outstanding question: once a participant is allocated
                  the answer is frozen (FACTOR_QUESTION_LOCKED), and inviting
                  someone into a form to change something the server will
                  refuse is worse than not offering the link.
                */}
                {onOpenQuestion &&
                row.questionId &&
                !status.allocated &&
                row.status !== "completed" ? (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => onOpenQuestion(row)}
                  >
                    {t("ui.participant.openQuestion")}
                  </Link>
                ) : null}
              </TableCell>

              {showValues ? (
                <TableCell>
                  <Typography variant="body2">
                    {row.valueLabel ?? row.valueToken ?? "—"}
                  </Typography>
                  {row.derivedValue != null ? (
                    <Typography variant="caption" color="text.secondary">
                      {t("ui.participant.factorDerivedValue", {
                        value: row.derivedValue,
                      })}
                    </Typography>
                  ) : null}
                </TableCell>
              ) : null}

              <TableCell>
                <Chip
                  size="small"
                  variant={row.status === "completed" ? "filled" : "outlined"}
                  color={STATUS_COLOR[row.status]}
                  label={t(`ui.participant.status.${row.status}`)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/*
        THE TWO ANSWERED-BUT-UNUSABLE STATES GET THEIR OWN EXPLANATION, because
        their remedy is not the obvious one. `not_covered` and `out_of_range`
        both mean the answer is in and the DESIGN does not account for it — a
        choice left out of the levels, or a value past the last band. Chasing
        the site for an answer that already exists wastes their time; what is
        needed is an amendment.
      */}
      {status.factors.some(
        (row) => row.status === "not_covered" || row.status === "out_of_range",
      ) ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t("ui.participant.factorDesignGap")}
        </Alert>
      ) : null}
    </Box>
  );
};
